/*global YT*/
/**
 * This uses the YouTube iframe player to embed a video.
 * https://developers.google.com/youtube/iframe_api_reference
 * CAVEATS:
 *  - Requires in HTML: <div id="video-container"><div id="player"></div></div>
 */

import $ from 'jquery';
import ProjectUtil from '../utils/project-util.js';

class YTPlayer {
	constructor() {
		this.initialized = false;
		this.player = null;
		this.vidID = null;
		this.dur = null;
		this.state = -1; // -1: unstarted, 0: ended, 1: playing, 2: paused, 3: buffering, 5: video cued.
		this.midTracked = null;
		this.timeout = null;
		this.initialPlayed = false;
		this.playerReady = false;
		this.trackID = null;
		this.playstate = "none";
		this.events = {
			start: [],
			midpoint: [],
			end: [],
			pause: [],
			resume: []
		};
	}
	dispatchEvent(eventID, scope) {
		var self = scope || this;
		if (self.events[eventID]) {
			for (var i = self.events[eventID].length - 1; i >= 0; i--) {
				try {
					self.events[eventID][i].call(null, self.vidID);
				} catch (er) {
					console.warn(er.message);
				}
			}
		}
	}

	static get defaultPlayerVars() {

		if (!this._defaultPlayerVars) {
			// PlayerVars documentation: https://developers.google.com/youtube/player_parameters
			this._defaultPlayerVars = {
				'autohide': 1, // autohide controls - 1 = always
				'autoplay': 0, // autoplay video - 0 = don't autoplay
				'controls': 2, // show controls - 2 = show controls after playback is initiated
				'disablekb': 1, // disable keyboard 1 = enabled
				"enablejsapi": 1, // enablejsapi - always 1 or this won't work
				'iv_load_policy': 3, // show video annotations - 3 = don't show
				'modestbranding': 0, // less youtube branding - 0 = not modest branding (1 is actually worse)
				"origin": document.domain, // security policy stuff
				"rel": 0, // show related videos at the end - 0 = don't show related
				'showinfo': 0, // show the video title, Watch Later, Share - 0 = don't show info
			};
		}
		return this._defaultPlayerVars;
	}

	//for $el, pass the jQuery selector $('#video-container')
	initPlayer(elID, videoID, playerVars = {}) {
		// prevent accidental re-init
		if (this.initialized || window.youTubeIframeAPILoaded) {
			this.onYouTubeIframeAPIReady(elID, videoID, playerVars, this);
			return;
		}
		// push this player into a global array
		if (!window.ytplayers) {
			window.ytplayers = [];
		}
		window.ytplayers.push({ scope: this, el: elID, id: videoID, playerVars: playerVars, cb: this.onYouTubeIframeAPIReady });
		//add window listener, because Google feels that's necessary
		window.onYouTubeIframeAPIReady = function () {
			window.youTubeIframeAPILoaded = true;
			ProjectUtil.youtubeInitialized = true;
			var totalPlayers,
				i;
			if (window.ytplayers) {
				totalPlayers = window.ytplayers.length;
				for (i = 0; i < totalPlayers; i++) {
					window.ytplayers[i].cb.call(null, window.ytplayers[i].el, window.ytplayers[i].id, window.ytplayers[i].playerVars, window.ytplayers[i].scope);
				}
			}
		};
	}

	loadApi() {
		$.getScript('//www.youtube.com/iframe_api', function () { window.youTubeIframeAPILoaded = true; });
	}

	//Automatically triggered by successful load of YT API
	onYouTubeIframeAPIReady(elID, videoID, tPlayerVars, scope) {
		// this function is called from window, so scope is passed to maintain reference to 'this'
		scope.initialized = true;
		scope.vidID = videoID;
		if (scope.player) {
			scope.player.loadVideoById(videoID, 0, 'large');
		} else {
			scope.player = new YT.Player(elID, {
				height: '100%',
				width: '100%',
				videoId: videoID,
				playerVars: $.extend({}, YTPlayer.defaultPlayerVars, tPlayerVars),
				//host: 'https://www.youtube-nocookie.com',
				host: 'https://www.youtube.com',

				events: {
					'onReady': function () {
						scope.onPlayerReady(scope);
						if (tPlayerVars.onPlayerReady) {
							tPlayerVars.onPlayerReady.call();
						}
					},
					'onStateChange': function (e) {
						scope.onPlayerStateChange(e, scope);
					}
				}
			});
		}
	}

	// 4. The API will call this function when the video player is ready.
	onPlayerReady(scope) {
		scope.midTracked = false;
		scope.playerReady = true;
		//scope.player.cueVideoById(scope.vidID, 0, "hd720");
		//this.player.mute();
	}

	// 5. The API calls this function when the player's state changes.
	/*
		-1 (unstarted)
		0 (ended)
		1 (playing)
		2 (paused)
		3 (buffering)
		5 (video cued).
	*/
	onPlayerStateChange(event, scope) {
		if (window.onPlayerStateChange) {
			window.onPlayerStateChange(event);
		}
		clearInterval(scope.timeout);
		//clear duration for new videos.
		if (event.data === -1) {
			//get video duration
			scope.dur = 0;
		}
		//If vid is unstarted, has ended or cued reset the video so we can track vid start
		if (event.data === -1 || event.data === 0 || event.data === 5) {
			this.playstate = "cued"; //0 is "ended", updated further down
			scope.initialPlayed = false;
		}

		//If initialPlayed hasn't happened AND status is set to 1 (playing) go ahead and track this
		if (!scope.initialPlayed && event.data === 1) {
			this.playstate = "playing";
			scope.initialPlayed = true;
			scope.midTracked = false;
			dispatchEvent('start', scope);
		} else if (event.data === 1) {
			this.playstate = "playing";
			// video is playing, but this isn't the start (because of scope.initialPlayed)
			dispatchEvent('resume', scope);
		}

		// check for pause: 2
		if (event.data === 2) {
			this.playstate = "paused";
			dispatchEvent('pause', scope);
		}

		if (event.data === 3) {
			this.playstate = "buffering";
		}

		// check for video complete: 0
		if (event.data === 0) {
			this.playstate = "complete";
			dispatchEvent('end', scope);
		}
		// track for midpoint
		if (event.data === 1 && !scope.midTracked) {
			scope.timeout = setInterval(function () {
				scope.checkForMidpoint(scope);
			}, 500);
		}
		// store state
		scope.state = event.data;
	}

	cueVideo(ytID, scope) {
		scope.vidID = ytID;
		if (scope.playerReady) {
			//player is ready.  Cue the video.
			scope.player.cueVideoById(ytID);
		}
	}
	playVideo() {

		this.player.playVideo();
	}

	pauseVideo() {
		this.player.pauseVideo();
	}

	checkForMidpoint(scope) {
		//get video duration
		if (!this.dur || this.dur === 0) {
			this.dur = scope.player.getDuration();
			//don't check until a duration is available.
			if (!this.dur || this.dur === 0) {
				return;
			}
		}
		var curTime = this.player.getCurrentTime();
		if (curTime > this.dur / 2) {
			this.midTracked = true;
			dispatchEvent('midpoint', this);
			clearInterval(this.timeout);
		}
	}

	replayVideo() {
		if (this.playerReady) {
			this.player.seekTo(0, true);
		}
	}

	addEventListener(eventID, cb) {
		if (this.events[eventID]) {
			this.events[eventID].push(cb);
		}
	}

	removeEventListener(eventID, cb) {
		if (this.events[eventID]) {
			var tIndex = this.events[eventID].indexOf(cb);
			if (tIndex >= 0) {
				this.events[eventID].splice(tIndex, 1);
			}
		}
	}

	destroy() {
		this.player.destroy();
	}

	static extractIdFromUrl(url) {
		//this Regex will split out the url into the [protocol/subdomain/youtube.com] block and the [id] block.
		const regex = /(?:http[s]?:\/\/)*(?:[^\.]+\.)*(?:youtube\.com\/(?:v\/|watch\?(?:.*?\&)?v=|embed|live\/)|youtu.be\/)([\w\-\_]+)/i;
		const matches = url.match(regex);
		return matches ? matches[1] : null;
	}
}

export default YTPlayer;